import { intersect, shape } from 'svg-intersections';
import VectorUtils from './vectorUtils';
var RAY_LENGTH = 999;
var IntersectionUtils = {
    /**
     *  Desired result is multiple parallel links between two nodes, as shown here:
     *                         _________
     *                        |         |
     *                        |  Node1  |
     *                        |_________|
     *                           |   |
     *                     Link1 |   | Link2
     *                  _________|___|__________
     *                 |                        |
     *                 |         Node2          |
     *                 |________________________|
     *
     *
     *  1. get the direction between the center of the two nodes (c)
     *
     *                             |
     *                         ____|____
     *                        |    |    |
     *                  Node1 |    c    |
     *                        |____|____|
     *                             |
     *                             |
     *                  ___________|____________
     *                 |           |            |
     *           Node2 |           c            |
     *                 |___________|____________|
     *                             |
     *                             |
     *
     *
     *  2. Make a line through the center of each node, perpendicular to direction from step 1
     *     Find the intersection points (X)
     *
     *                         _________
     *                        |         |
     *            ------------X----c----X-------------
     *                        |_________|
     *
     *
     *                  ________________________
     *                 |                        |
     *     ------------X-----------c------------X-------------
     *                 |________________________|
     *
     *
     *  3. take the smaller line and use for both nodes. The links' start/end points will be "spread" over these lines
     *
     *                         _________
     *                        |         |
     *                        |----c----|
     *                        |_________|
     *
     *
     *                  ________________________
     *                 |                        |
     *                 |       ----c----        |
     *                 |________________________|
     *
     *
     * 4. split these lines based on number of links, get points (p) which will be start/end points of links
     *
     *                         _________
     *                        |         |
     *                        |--p---p--|
     *                        |_________|
     *
     *
     *                  ________________________
     *                 |                        |
     *                 |       --p---p--        |
     *                 |________________________|
     *
     *
     * 5. Actually we want links to start/end on node edge, so now create lines between start and end points, then find the intersections (X) with the node edges
     *
     *                         _________
     *                        |         |
     *                        |--p---p--|
     *                        |__|___|__|
     *                           X   X
     *                           |   |
     *                  _________X___X__________
     *                 |         |   |          |
     *                 |       --p---p--        |
     *                 |________________________|
     *
     *
     *  Now we have 2 start points and 2 end points to return, so links can be drawn as in desired result:
     *                         _________
     *                        |         |
     *                        |  Node1  |
     *                        |_________|
     *                           |   |
     *                     Link1 |   | Link2
     *                  _________|___|__________
     *                 |                        |
     *                 |         Node2          |
     *                 |________________________|
     *
     */
    findNodePoints: function (leftNodeShape, leftNodePosition, rightNodeShape, rightNodePosition, numPoints) {
        // 0. get shapes of both nodes and the direction between them, so we can calculate intersection points
        var directionBetweenNodes = IntersectionUtils.getDirectionBetweenNodes(leftNodePosition, rightNodePosition);
        // 1. if we are only drawing 1 link, get the intersection points of a center-center line and save ourselves the rest of the calculations
        if (numPoints === 1) {
            var leftNodeIntersection = IntersectionUtils.getIntersectionInDirection(leftNodeShape, directionBetweenNodes);
            var rightNodeIntersection = IntersectionUtils.getIntersectionInDirection(rightNodeShape, VectorUtils.reverseVector(directionBetweenNodes));
            return {
                leftNodePoints: [VectorUtils.addVectorVector(leftNodeIntersection, leftNodePosition)],
                rightNodePoints: [VectorUtils.addVectorVector(rightNodeIntersection, rightNodePosition)],
            };
        }
        // 2. create a line at right-angles to this direction
        var perpendicularNormal = VectorUtils.rotateVector90(directionBetweenNodes);
        // 3. get the distance across each node in this direction, these distances are the "available width" to spread links over
        var availableLeftNodeWidth = IntersectionUtils.getWidthOfNodeInDirection(leftNodeShape, perpendicularNormal);
        var availableRightNodeWidth = IntersectionUtils.getWidthOfNodeInDirection(rightNodeShape, perpendicularNormal);
        // 3b. take the smallest of the two values -- links should be parallel, therefore if nodes are different sizes, treat both as the smaller size
        var availableWidth = availableLeftNodeWidth < availableRightNodeWidth ? availableLeftNodeWidth : availableRightNodeWidth;
        // 4. create points along this line, equally spaced apart. These will be start/end points of lines along which links will be drawn
        var linkLineEndPoints = IntersectionUtils.getPointsSpreadOverLine(numPoints, perpendicularNormal, availableWidth);
        // 5. Draw lines between start and end points, and find intersections with node shape, so we know where on node edge to draw arrowheads
        var leftNodePoints = linkLineEndPoints
            .map(function (point) { return IntersectionUtils.getIntersectionInDirectionFromPoint(leftNodeShape, directionBetweenNodes, point); });
        var rightNodePoints = linkLineEndPoints
            .map(function (point) { return IntersectionUtils.getIntersectionInDirectionFromPoint(rightNodeShape, VectorUtils.reverseVector(directionBetweenNodes), point); });
        return {
            leftNodePoints: leftNodePoints.map(function (localPoint) { return VectorUtils.addVectorVector(localPoint, leftNodePosition); }),
            rightNodePoints: rightNodePoints.map(function (localPoint) { return VectorUtils.addVectorVector(localPoint, rightNodePosition); }),
        };
    },
    getDirectionBetweenNodes: function (sourcePosition, targetPosition) {
        var sourceTargetLine = VectorUtils.vectorFromTo(sourcePosition, targetPosition);
        return VectorUtils.normaliseVector(sourceTargetLine);
    },
    // starting at the center of the node, return the intersection of node edge and the passed direction
    getIntersectionInDirection: function (nodeShape, direction) {
        var origin = { x: 0, y: 0 };
        return IntersectionUtils.getIntersectionInDirectionFromPoint(nodeShape, direction, origin);
    },
    // starting at the passed point within the node, return the intersection of node edge and the passed direction
    getIntersectionInDirectionFromPoint: function (nodeShape, direction, rayStart) {
        var rayEnd = VectorUtils.addVectorVector(rayStart, VectorUtils.multiplyVectorScalar(direction, RAY_LENGTH));
        var intersections = IntersectionUtils.intersectLineShape(rayStart, rayEnd, nodeShape);
        return intersections.points[0] || rayStart; // if there is no edge intersection (case: overlapping nodes), simply return the start point
    },
    // given a direction, draw a line in that direction that crosses the center of the node. return length of this line "across" the node
    getWidthOfNodeInDirection: function (nodeShape, direction) {
        var pointOne = IntersectionUtils.getIntersectionInDirection(nodeShape, direction);
        var pointTwo = IntersectionUtils.getIntersectionInDirection(nodeShape, VectorUtils.reverseVector(direction));
        return VectorUtils.distancePointPoint(pointOne, pointTwo);
    },
    // given a line through the origin, return n points spread evenly along this line
    getPointsSpreadOverLine: function (numPoints, lineDirection, lineLength) {
        var startPoint = VectorUtils.multiplyVectorScalar(lineDirection, lineLength * -0.5);
        var segmentLength = lineLength / (numPoints + 1);
        var resultPoints = [];
        for (var i = 1; i < numPoints + 1; i++) {
            var shift = VectorUtils.multiplyVectorScalar(lineDirection, segmentLength * i);
            resultPoints.push(VectorUtils.addVectorVector(startPoint, shift));
        }
        return resultPoints;
    },
    addOffsetToPoint: function (point, direction, offset) {
        return VectorUtils.addVectorVector(point, VectorUtils.multiplyVectorScalar(direction, offset));
    },
    intersectLineShape: function (p1, p2, nodeShape) {
        return intersect(shape('line', {
            x1: p1.x,
            y1: p1.y,
            x2: p2.x,
            y2: p2.y,
        }), shape(nodeShape.name, nodeShape.attrs));
    },
};
export default IntersectionUtils;
//# sourceMappingURL=intersectionUtils.js.map